<template><div><code-well-header>
  <dt-stack direction="row" align="center" class="d-toggle-group">
    <dt-toggle label-class="d-mr6">Label</dt-toggle>
  </dt-stack>
</code-well-header>
<!-- <component-combinator component-name="DtToggle" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The Toggle component acts as a way to allow the User to switch between two mutually exclusive options. While it technically mirrors a <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink> state, its effect on the system is immediate—much like a light switch immediately turns on or off the lights. In contrast, the checked state of a <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink> won't be applied until a separate action to confirm the selection is taken.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When its action has an instantaneous effect.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When its action does not have an immediate effect on the application.</li>
<li class="d-docsite--list-element">Selecting between 2 options. Instead, utilize a <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink>.</li>
<li class="d-docsite--list-element">As an alternative to a <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink> or <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink> within a Form.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">A Toggle component should be used as a control within an application and provide a way to toggle between two states like a household light switch.</li>
<li class="d-docsite--list-element">An <code v-pre>indeterminate</code> Toggle convey a &quot;mixed&quot; state that neither qualifies as toggled or not toggled. An example use case is when a Toggle acts as a &quot;parent&quot; of a collection of child Toggle components:
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Toggled: all children are toggled.</li>
<li class="d-docsite--list-element">Not toggled: all children are not toggled.</li>
<li class="d-docsite--list-element"><code v-pre>indeterminate</code>: children are a mix of toggled and not toggled.</li>
</ul>
</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack as="fieldset" gap="400">
    <dt-toggle label-class="d-mr6">Unchecked Toggle</dt-toggle>
    <dt-toggle :model-value="true" label-class="d-mr6">Checked Toggle</dt-toggle>
    <dt-toggle disabled label-class="d-mr6">Unchecked Disabled</dt-toggle>
    <dt-toggle :model-value="true" disabled label-class="d-mr6">Checked Disabled</dt-toggle>
    <dt-toggle :model-value="mixed" label-class="d-mr6">Indeterminate Toggle</dt-toggle>
    <dt-toggle :model-value="mixed" label-class="d-mr6" disabled>Indeterminate Disabled</dt-toggle>
    <dt-toggle label-class="d-mr6" :show-icon="false">Without icon</dt-toggle>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-toggle-wrapper">
  <label for="dt3"> Unchecked Toggle </label><button id="dt3" role="switch" type="button" aria-checked="false" aria-disabled="false" class="d-toggle"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt4"> Checked Toggle </label>
  <button id="dt4" role="switch" type="button" aria-checked="true" aria-disabled="false" class="d-toggle d-toggle--checked"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt5"> Unchecked Disabled </label>
  <button id="dt5" role="switch" type="button" aria-checked="false" aria-disabled="true" class="d-toggle d-toggle--disabled" disabled="disabled"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt6"> Checked Disabled </label>
  <button id="dt6" role="switch" type="button" aria-checked="true" disabled="disabled" aria-disabled="true" class="d-toggle d-toggle--checked d-toggle--disabled"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt7"> Indeterminate Toggle </label>
  <button id="dt7" role="checkbox" type="button" aria-checked="mixed" aria-disabled="false" class="d-toggle d-toggle--indeterminate"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt8"> Indeterminate Disabled </label>
  <button id="dt8" role="checkbox" type="button" aria-checked="mixed" disabled="disabled" aria-disabled="true" class="d-toggle d-toggle--disabled d-toggle--indeterminate"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt14">Without icon</label>
  <button id="dt14" role="switch" type="button" aria-checked="false" aria-disabled="false" class="d-toggle"></button>
</div>
'
vueCode='
<dt-toggle>
  Unchecked Toggle
</dt-toggle>
<dt-toggle :model-value="true">
  Checked Toggle
</dt-toggle>
<dt-toggle :disabled="true">
  Unchecked Disabled
</dt-toggle>
<dt-toggle :model-value="true" :disabled="true">
  Checked Disabled
</dt-toggle>
<dt-toggle model-value="mixed">
  Indeterminate Toggle
</dt-toggle>
<dt-toggle model-value="mixed" :disabled="true">
  Indeterminate Disabled
</dt-toggle>
<dt-toggle :show-icon="false">
  Without icon
</dt-toggle>
'
showHtmlWarning />
<h3 id="sizes" tabindex="-1" class="d-docsite--header-3">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack as="fieldset" gap="400">
    <dt-toggle size="sm" label-class="d-mr6">Small size</dt-toggle>
    <dt-toggle label-class="d-mr6">Default size</dt-toggle>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-toggle-wrapper">
  <label for="dt3"> Default size </label><button id="dt3" role="switch" type="button" aria-checked="false" aria-disabled="false" class="d-toggle"><span class="d-toggle__inner"></span></button>
</div>
<div class="d-toggle-wrapper">
  <label for="dt14"> Small size </label><button id="dt14" role="switch" type="button" aria-checked="false" aria-disabled="false" class="d-toggle d-toggle--small"><span class="d-toggle__inner"></span></button>
</div>
'
vueCode='
<dt-toggle>
  Default size
</dt-toggle>
<dt-toggle size="sm">
  Small size
</dt-toggle>
'
showHtmlWarning />
<h3 id="with-v-model" tabindex="-1" class="d-docsite--header-3">With v-model <a class="header-anchor d-link d-docsite--link d-link" href="#with-v-model" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack as="fieldset" gap="400">
    <dt-toggle v-model="checked" label-class="d-mr6" ref="vModelExample">Toggle</dt-toggle>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.vModelExample"
vueCode='
<dt-toggle v-model="checked">
  Toggle
</dt-toggle>
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="toggle" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="toggle" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">This component uses a native button element under the hood that has a role <code v-pre>switch</code> and type <code v-pre>button</code> to improve accessibility.
<a href="https://www.w3.org/TR/2021/NOTE-wai-aria-practices-1.2-20211129/examples/switch/switch-button.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">See W3C guidelines</a>
for more information.</p>
<p class="d-docsite--paragraph">The best accessibility is semantic HTML. Most screen readers understand how to parse buttons if they’re correctly formatted. When it comes to toggles, there are a few things to keep in mind:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">All toggle buttons should have an <code v-pre>id</code> attribute.</li>
<li class="d-docsite--list-element">Associate toggle labels with their buttons using the <code v-pre>for</code> attribute. This correlates with the toggle's <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">If you have a group of related toggles, use a <code v-pre>fieldset</code> to group them and a <code v-pre>legend</code> to title the group. For further information, please visit Gov.UK’s article, <a href="https://accessibility.blog.gov.uk/2016/07/22/using-the-fieldset-and-legend-elements" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">&quot;Using the fieldset and legend elements&quot;</a>.</li>
</ul>
<h3 id="button" tabindex="-1" class="d-docsite--header-3">Button <a class="header-anchor d-link d-docsite--link d-link" href="#button" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The button element should be fully accessible by keyboard. To accomplish this, the <code v-pre>DtToggle</code> component automatically
populates several ARIA attributes to the underlying <code v-pre>button</code> element depending on the checked and
disabled states. However, if a label default slot is not used with <code v-pre>DtToggle</code>
(without including an <code v-pre>aria-label</code>), a console warning error will be thrown indicating that this issue exists.</p>
<h3 id="focus-keyboard" tabindex="-1" class="d-docsite--header-3">Focus &amp; Keyboard <a class="header-anchor d-link d-docsite--link d-link" href="#focus-keyboard" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The button element should capture keyboard focus as long as it is not disabled (<code v-pre>disabled</code> prop is <code v-pre>true</code>).
<code v-pre>ENTER</code> key will
emit a <strong>change</strong> event with the current value of the toggle and will change its internal <code v-pre>checked</code> state.</p>
</div></template>


<script setup>
import { ref } from 'vue'

const checked = ref(false)
</script>